import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { useReducedMotion } from 'framer-motion';
import { useScrollSequence } from '../hooks/useScrollSequence';

function AnimSequence({
  id,
  duration,
  frames,
  imageSequenceUrl,
  fitWidth = false,
  captionIndex,
  captionTitle,
  captionBody,
}) {
  const shouldReduce    = useReducedMotion();
  const containerRef    = useRef(null);
  const canvasRef       = useRef(null);
  const framesRef       = useRef([]);      // HTMLImageElement cache
  const canvasSizedRef  = useRef(false);   // canvas resized only once
  const currentIdxRef   = useRef(0);       // latest requested frameIndex

  const [ready, setReady] = useState(false);

  const { progress, frameIndex } = useScrollSequence(containerRef, frames);

  const pct = Math.round(progress * 100);

  /* ── Draw from cache (synchronous, no network wait) ── */
  const drawFrame = useCallback((idx) => {
    const canvas = canvasRef.current;
    const img    = framesRef.current[idx];
    if (!canvas || !img) return;

    // Size canvas once from the first image that arrives
    if (!canvasSizedRef.current) {
      canvas.width          = img.naturalWidth;
      canvas.height         = img.naturalHeight;
      canvasSizedRef.current = true;
    }

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
  }, []);

  /* ── Preload ALL frames into cache ── */
  useEffect(() => {
    if (!imageSequenceUrl) return;

    framesRef.current  = new Array(frames).fill(null);
    canvasSizedRef.current = false;
    setReady(false);
    let cancelled = false;

    for (let i = 0; i < frames; i++) {
      const img = new Image();
      const idx = i;
      img.onload = () => {
        if (cancelled) return;
        framesRef.current[idx] = img;

        // First frame → size canvas and show
        if (idx === 0) {
          drawFrame(0);
          setReady(true);
        }

        // If this frame arrived and is the one currently needed → draw it
        if (idx === currentIdxRef.current && idx !== 0) {
          drawFrame(idx);
        }
      };
      img.src = imageSequenceUrl(i);
    }

    return () => { cancelled = true; };
  }, [imageSequenceUrl, frames, drawFrame]);

  /* ── On scroll: draw from cache (instant) or wait for preload ── */
  useEffect(() => {
    if (!imageSequenceUrl) return;
    currentIdxRef.current = frameIndex;
    if (framesRef.current[frameIndex]) {
      drawFrame(frameIndex);   // already cached → zero-latency draw
    }
    // Not yet cached → the preload onload above will draw it when it arrives
  }, [frameIndex, imageSequenceUrl, drawFrame]);

  /* ── Custom event for external consumers ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.dispatchEvent(new CustomEvent('oid:sequenceProgress', {
      bubbles: true,
      detail: { sequenceId: id, progress, frameIndex, totalFrames: frames },
    }));
  }, [id, progress, frameIndex, frames]);

  const showCanvas      = imageSequenceUrl && ready;
  const showPlaceholder = !imageSequenceUrl;
  const loadingPending  = imageSequenceUrl && !ready;

  return (
    <Box
      ref={containerRef}
      data-sequence={id}
      data-progress={progress}
      data-frame-index={frameIndex}
      height={`${duration * 100}vh`}
      position="relative"
    >
      {/* ── Sticky viewport container ── */}
      <Box
        position="sticky" top={0} height="100vh"
        overflow="hidden" bg="oid.bg"
        display="flex" flexDirection="column"
        alignItems="center" justifyContent="center"
      >

        {/* ── Overlay elements constrained to 1600px grid ── */}
        <Box position="absolute" inset={0} zIndex={10} pointerEvents="none">
          <Box maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }}
            height="100%" position="relative">

            {/* Bottom-left: caption */}
            <Box position="absolute"
              bottom={{ base: '72px', md: 8 }} left={{ base: 6, lg: 12 }}
              maxWidth={{ base: 'calc(50vw)', md: '340px' }} pointerEvents="auto">
              <Text fontSize="10px" fontWeight={600} letterSpacing="0.24em"
                textTransform="uppercase" color="oid.accent" mb={2}>
                {captionIndex}
              </Text>
              <Text fontWeight={700} color="oid.text" lineHeight={1.2} mb={2}
                sx={{ fontSize: 'clamp(16px, 2.2vw, 26px)' }}>
                {captionTitle}
              </Text>
              <Text fontSize="xs" fontWeight={400} color="oid.muted"
                lineHeight={1.618}>
                {captionBody}
              </Text>
            </Box>

            {/* Bottom-right: scroll progress bar */}
            <Box position="absolute" bottom={{ base: 6, md: 8 }}
              right={{ base: 6, lg: 12 }}
              display="flex" flexDirection="column" alignItems="flex-end" gap={2}>
              <Box width="100px" height="2px" bg="rgba(22,33,58,0.12)"
                position="relative" overflow="hidden" aria-hidden="true">
                <Box position="absolute" left={0} top={0} height="100%" bg="oid.accent"
                  style={{ width: `${pct}%`,
                    transition: shouldReduce ? 'none' : 'width 0.1s linear' }} />
              </Box>
              <Text fontSize="9px" fontWeight={600} letterSpacing="0.3em"
                textTransform="uppercase" color="rgba(22,33,58,0.3)" aria-hidden="true">
                Scroll to advance
              </Text>
            </Box>

          </Box>
        </Box>

        {/* ── Main image area ── */}
        <Box
          className="anim-image-area"
          position="absolute" inset={0}
          border={showCanvas ? 'none' : '1px solid rgba(22,33,58,0.1)'}
          overflow="hidden"
          role={showCanvas ? undefined : 'img'}
          aria-label={showCanvas ? undefined : `${captionTitle} — animation sequence placeholder`}
        >
          {/* ── CANVAS ── */}
          {imageSequenceUrl && (
            <Box position="absolute" inset={0}
              display="flex" alignItems="center" justifyContent="center"
              overflow="hidden">
              <canvas
                ref={canvasRef}
                style={{
                  display:    'block',
                  width:      fitWidth ? '100%' : 'auto',
                  height:     fitWidth ? 'auto' : '100%',
                  opacity:    showCanvas ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
                aria-label={`${captionTitle} — frame ${frameIndex + 1} of ${frames}`}
              />
            </Box>
          )}

          {/* ── PLACEHOLDER ── */}
          {showPlaceholder && (
            <>
              <Box position="absolute" inset={0}
                sx={{
                  backgroundImage: `
                    repeating-linear-gradient(0deg, transparent,
                      transparent calc(100%/8 - 0.5px),
                      rgba(22,33,58,0.06) calc(100%/8 - 0.5px),
                      rgba(22,33,58,0.06) calc(100%/8)),
                    repeating-linear-gradient(90deg, transparent,
                      transparent calc(100%/14 - 0.5px),
                      rgba(22,33,58,0.06) calc(100%/14 - 0.5px),
                      rgba(22,33,58,0.06) calc(100%/14))`,
                }}
                zIndex={1} pointerEvents="none" />
              <Flex position="absolute" inset={0} zIndex={2}
                flexDirection="column" alignItems="center" justifyContent="center" gap={3}>
                <Text fontSize="10px" fontWeight={600} letterSpacing="0.38em"
                  textTransform="uppercase" color="rgba(22,33,58,0.25)">
                  Image Sequence Placeholder
                </Text>
                <Text fontSize="9px" fontWeight={400} letterSpacing="0.12em"
                  color="rgba(22,33,58,0.15)" textAlign="center">
                  {id} · {duration}s · ~{frames} frames
                </Text>
                <Text fontWeight={700} color="rgba(22,33,58,0.07)"
                  sx={{ fontSize: 'clamp(52px, 9vw, 130px)' }}
                  lineHeight={1} letterSpacing="-0.04em" aria-hidden="true">
                  {String(pct).padStart(3, '0')}
                </Text>
              </Flex>
            </>
          )}

          {/* ── LOADING STATE ── */}
          {loadingPending && (
            <Flex position="absolute" inset={0} zIndex={3}
              alignItems="center" justifyContent="center"
              flexDirection="column" gap={3}
              bg="oid.bg">
              <Text fontSize="10px" fontWeight={600} letterSpacing="0.38em"
                textTransform="uppercase" color="rgba(22,33,58,0.4)">
                Loading
              </Text>
              <Box width="32px" height="2px" bg="oid.accent"
                sx={{ animation: 'lineSwipe 1.2s ease-in-out infinite' }} />
            </Flex>
          )}
        </Box>

      </Box>
    </Box>
  );
}

export default AnimSequence;
